import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { apiRequest, incaAccessor } from '@cancercentrum/inca';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import invariant from 'invariant';
import { useSelector, useStore } from 'react-redux';
import usePromise from '../../hooks/usePromise';
import { LoadingAlert } from '../LoadingAlert';
import { AjaxErrorAlert } from '../AjaxErrorAlert';
import { getErrandConnection } from '../Connection/utils';
import { useConnectionOptional } from '../../hooks/useConnection';
import useConnectionState from '../unsafe_Connection/hooks/useConnectionState';
import { ErrandPanelComponent } from './index';
export const ErrandPanelContext = /*#__PURE__*/ createContext(null);
export const ErrandPanel = (props)=>{
    const { reportingPositions, topPositions, validate, validationPredicate, options = {}, errandComment, setOriginalComment, row } = props;
    const inca = incaAccessor();
    const registerId = inca.form.registerId;
    const store = useStore();
    const connectionState = useConnectionOptional();
    const superConnectionState = useConnectionState();
    const promiseCreator = useRef(async ()=>{
        return {
            errand: await apiRequest(`/Errands/${inca.form.data.errandId}`),
            connection: await getErrandConnection(inca.form.data.errandId),
            reportingPositions: reportingPositions ?? await apiRequest(`/Registers/${registerId}/ReportingPositions`),
            topPositions: topPositions ?? await apiRequest('/Positions/Top'),
            reviewers: await apiRequest(`/Registers/${registerId}/Reviewers`),
            reporters: await apiRequest(`/Registers/${registerId}/Reporters`)
        };
    });
    const blacklist = useSelector((state)=>state?.validation?.blacklist) ?? [];
    const formIncomplete = !!blacklist.length;
    const { exec, result, isLoading, error } = usePromise(promiseCreator.current);
    useEffect(()=>{
        if (result) {
            setOriginalComment(result.errand.comment);
        }
    }, [
        result,
        setOriginalComment
    ]);
    useEffect(()=>{
        const reload = ()=>exec(promiseCreator.current);
        inca.on('connection', reload);
        return ()=>{
            inca.off('connection', reload);
        };
    }, [
        inca,
        exec
    ]);
    const hasErrors = !!row.getErrors({
        includePristine: true,
        validationPredicate: validationPredicate
    }).filter((x)=>x.$errors.some((y)=>y.isFatal)).length;
    const value = useMemo(()=>{
        if (!result) {
            return null;
        }
        return {
            ...result,
            errandComment,
            formIncomplete,
            options,
            hasErrors,
            validate: async ()=>{
                return await validate() !== false;
            },
            reload: ()=>exec(promiseCreator.current),
            store,
            connectionState: connectionState,
            superConnectionState: superConnectionState
        };
    }, [
        result,
        errandComment,
        formIncomplete,
        options,
        hasErrors,
        store,
        connectionState,
        superConnectionState,
        validate,
        exec
    ]);
    return /*#__PURE__*/ _jsxs(ErrandPanelContext.Provider, {
        value: value,
        children: [
            isLoading && /*#__PURE__*/ _jsx(LoadingAlert, {}),
            error && /*#__PURE__*/ _jsx(AjaxErrorAlert, {
                error: error
            }),
            !isLoading && result && /*#__PURE__*/ _jsx(ErrandPanelComponent, {
                buttons: options.buttons
            })
        ]
    });
};
export const useErrandPanelContext = ()=>{
    const ctx = useContext(ErrandPanelContext);
    invariant(ctx, 'useErrandPanelContext requires a context to be created in a parent component');
    return ctx;
};
